import { useState, useEffect } from 'react'
import supabase from '../../supabase'
import { Link } from 'react-router-dom'
import Loader from '../../Common/Loader'
import logo from '../assets/logo.svg'
import './ForgotPassword.css'

function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [displayedText, setDisplayedText] = useState('')
  const [showCursor, setShowCursor] = useState(true)
  
  // Subtitle text for typing animation
  const subtitleText = "Your One-Stop Business Solution"

  // Typing animation effect
  useEffect(() => {
    let currentIndex = 0
    const typingInterval = setInterval(() => {
      if (currentIndex <= subtitleText.length) {
        setDisplayedText(subtitleText.slice(0, currentIndex))
        currentIndex++
      } else {
        clearInterval(typingInterval)
        setShowCursor(true)
      }
    }, 50)

    return () => clearInterval(typingInterval)
  }, [])

  const handleReset = async () => {
    if (!email) {
      setMessage('Please enter your email address.')
      setMessageType('error')
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setMessage('Please enter a valid email address.')
      setMessageType('error')
      return
    }

    setIsLoading(true)
    setMessage('')
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      })
      
      if (error) {
        if (error.message.includes('Email not confirmed')) {
          setMessage('Please confirm your email address first before resetting password.')
          setMessageType('info')
        } else if (error.message.includes('Email rate limit exceeded')) {
          setMessage('Too many reset attempts. Please wait before trying again.')
          setMessageType('error')
        } else {
          setMessage(error.message)
          setMessageType('error')
        }
      } else {
        setMessage('Password reset email sent! Check your inbox and spam folder.')
        setMessageType('success')
        setEmail('')
      }
    } catch (err) {
      setMessage('An unexpected error occurred. Please try again.')
      setMessageType('error')
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleReset()
    }
  }

  return (
    <div className="ForgotPassword-container">
      <div className="ForgotPassword-wrapper">
        {/* Left Section - Image and Subtitle */}
        <div className="ForgotPassword-left-section">
          <div className="ForgotPassword-image-container">
            <img 
              src={logo} 
              alt="Logo" 
              className="ForgotPassword-image"
            />
          </div>
          
          <div className="ForgotPassword-subtitle-container">
            <p className="ForgotPassword-subtitle">
              {displayedText}
              {showCursor && <span className="ForgotPassword-subtitle-cursor"></span>}
            </p>
          </div>
        </div>

        {/* Right Section - Forgot Password Form */}
        <div className="ForgotPassword-right-section">
          <div className="ForgotPassword-card">
            {isLoading && (
              <div className="ForgotPassword-loading-overlay">
                <Loader />
              </div>
            )}
            
            <div className="ForgotPassword-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            
            <h2 className="ForgotPassword-title">Forgot Password?</h2>
            <p className="ForgotPassword-description">
              No worries! Enter your email address and we'll send you a link to reset your password.
            </p>
            
            <form className="ForgotPassword-form" onSubmit={(e) => { e.preventDefault(); handleReset(); }}>
              <input 
                className="ForgotPassword-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter your email address"
                required
                disabled={isLoading}
              />
              
              <button 
                className="ForgotPassword-button"
                type="submit"
                disabled={isLoading || !email}
              >
                {isLoading ? <Loader /> : 'Send Reset Link'}
              </button>
            </form>

            {message && (
              <div className={`ForgotPassword-message ForgotPassword-message--${messageType}`}>
                {message}
              </div>
            )}

            <div className="ForgotPassword-links">
              <p className="ForgotPassword-link-text">
                Remember your password? <Link to="/login" className="ForgotPassword-link">Back to Sign In</Link>
              </p>
              <p className="ForgotPassword-link-text">
                Don't have an account? <Link to="/signup" className="ForgotPassword-link">Create Account</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ForgotPassword